"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModalViewContent = exports.ModalDomContext = void 0;
exports.default = ModalWrapper;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const native_1 = require("@react-navigation/native");
const react_native_1 = require("react-native");
const react_native_paper_1 = require("react-native-paper");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const Theme_1 = require("../Theme");
const ThemeContext_1 = require("../ThemeContext");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const react_native_safe_area_context_1 = require("react-native-safe-area-context");
exports.ModalDomContext = react_1.default.createContext(null);
const iconMap = {
    calendar: Theme_1.ICONS.Regular.Calendar,
    'chevron-left': Theme_1.ICONS.Regular.ChevronLeft,
    'chevron-right': Theme_1.ICONS.Regular.ChevronRight,
    'close': Theme_1.ICONS.Regular.Xmark,
    'menu-up': Theme_1.ICONS.Solid.CaretUp,
    'menu-down': Theme_1.ICONS.Solid.CaretDown,
    check: Theme_1.ICONS.Solid.Check,
};
const WebIcon = ({ name, ...props }) => {
    const icon = iconMap[name];
    if (!icon)
        return null;
    return ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: icon, ...props, size: 14 }) }));
};
const FOCUSABLE_SELECTOR = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';
const ModalViewContent = ({ onClose, navigation, needsConfirmation = false, onConfirmation, children, width, height, maxWidth, maxHeight, useNativeModal, }) => {
    const shouldUseNativeModal = useNativeModal || !navigation;
    const THEME = (0, ThemeContext_1.useTheme)();
    const PaperTheme = (0, Theme_1.useGlobalPaperTheme)();
    const safe = (0, react_native_safe_area_context_1.useSafeAreaInsets)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const safeBottom = safe?.bottom + 12;
    const safeRight = safe?.right + 12;
    const safeLeft = safe?.left + 12;
    const [modalDomNode, setModalDomNode] = react_1.default.useState(null);
    const [snackVisibility, setSnackVisibility] = react_1.default.useState(false);
    const [snackType, setSnackType] = react_1.default.useState('none');
    const [snackMessage, setSnackMessage] = react_1.default.useState('');
    const dialogContainerRef = react_1.default.useRef(null);
    react_1.default.useEffect(() => {
        if (navigation && typeof navigation.canGoBack === 'function' && !navigation.canGoBack()) {
            navigation.dispatch(native_1.CommonActions.reset({
                index: 0,
                routes: [{
                        name: 'Content',
                        state: { routes: [{ name: 'Home' }] }
                    }]
            }));
        }
    }, [navigation]);
    const handleClose = react_1.default.useCallback(() => {
        if (needsConfirmation && onConfirmation) {
            onConfirmation();
            return;
        }
        if (onClose) {
            onClose();
            return;
        }
        navigation?.goBack?.();
    }, [needsConfirmation, onConfirmation, onClose, navigation]);
    const showSnackbar = (eventData) => {
        setSnackVisibility(true);
        setSnackType(eventData.type);
        setSnackMessage(eventData.message);
    };
    const dismissSnackbar = () => {
        setSnackVisibility(false);
        setSnackType("none");
        setSnackMessage("");
    };
    react_1.default.useEffect(() => {
        if (react_native_1.Platform.OS !== "ios")
            return;
        const sub = react_native_1.DeviceEventEmitter.addListener("event.showSnackbar", (eventData) => showSnackbar(eventData));
        return () => {
            sub.remove();
        };
    }, []);
    const paperSettings = react_native_1.Platform.OS === 'web' ? { icon: WebIcon } : {};
    const isSmallScreen = size === 'sm';
    const dialogStyle = isSmallScreen
        ? {
            backgroundColor: THEME.CardBackground,
            width: '100%',
            height: '100%',
            flex: 1,
            flexDirection: 'column',
        }
        : {
            backgroundColor: THEME.CardBackground,
            borderRadius: 4,
            minWidth: 480,
            minHeight: 0,
            maxWidth: maxWidth ?? width ?? 600,
            maxHeight: maxHeight ?? height ?? '80%',
            width,
            height,
            overflow: 'hidden',
            elevation: 8,
        };
    const dialogRefCallback = react_1.default.useCallback((node) => {
        dialogContainerRef.current = node;
        if (react_native_1.Platform.OS === 'web' && node) {
            const domElement = node._nativeTag || node;
            if (domElement instanceof HTMLElement) {
                setModalDomNode(domElement);
            }
        }
    }, []);
    const statusBarHeight = react_native_1.Platform.OS === 'android' ? (react_native_1.StatusBar.currentHeight || 0) : safe?.top;
    return ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.overlay, pointerEvents: "box-none", children: (0, jsx_runtime_1.jsx)(exports.ModalDomContext.Provider, { value: modalDomNode, children: (0, jsx_runtime_1.jsx)(react_native_paper_1.PaperProvider, { theme: PaperTheme, settings: paperSettings, children: (0, jsx_runtime_1.jsxs)(react_native_1.KeyboardAvoidingView, { behavior: react_native_1.Platform.OS === 'ios' ? 'padding' : undefined, style: [react_native_1.StyleSheet.absoluteFill, { marginTop: shouldUseNativeModal ? statusBarHeight : undefined, backgroundColor: isSmallScreen ? THEME.CardBackground : 'transparent' }], children: [!isSmallScreen && ((0, jsx_runtime_1.jsx)(react_native_1.Pressable, { tabIndex: -1, accessible: false, importantForAccessibility: "no-hide-descendants", style: [react_native_1.StyleSheet.absoluteFill, { backgroundColor: '#00000044' }], onPress: (e) => {
                                e.stopPropagation();
                                handleClose();
                            } })), (0, jsx_runtime_1.jsx)(react_native_1.View, { ref: dialogRefCallback, role: "dialog", "aria-modal": true, accessibilityViewIsModal: true, tabIndex: -1, style: {
                                ...dialogStyle,
                                margin: isSmallScreen ? 0 : 'auto',
                                display: 'flex',
                                justifyContent: isSmallScreen ? undefined : 'center',
                                paddingBottom: safeBottom,
                                paddingTop: 12,
                                paddingRight: safeRight,
                                paddingLeft: safeLeft,
                            }, children: children }), react_native_1.Platform.OS === 'ios' && ((0, jsx_runtime_1.jsx)(react_native_paper_1.Snackbar, { wrapperStyle: { bottom: 12 }, style: {
                                backgroundColor: snackType === 'error'
                                    ? THEME.ToastError
                                    : snackType === 'warning'
                                        ? THEME.WarningLightest
                                        : THEME.PrimaryLightest,
                            }, visible: snackVisibility, onDismiss: dismissSnackbar, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.snackbarContent, children: [snackType !== 'none' && ((0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { size: 24, icon: snackType === "success" ? Theme_1.ICONS.Solid.CircleCheck : Theme_1.ICONS.Solid.CircleExclamation, color: snackType === "error" ? THEME.ToastErrorText : (snackType === "warning" ? THEME.Warning : THEME.Primary), style: { marginRight: 12, outlineWidth: 0 } })), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: {
                                            color: snackType === 'error'
                                                ? THEME.ToastErrorText
                                                : snackType === 'warning'
                                                    ? THEME.Warning
                                                    : THEME.Primary,
                                            textAlignVertical: 'center',
                                            overflow: 'hidden',
                                            flex: 1,
                                        }, children: snackMessage })] }) }))] }) }) }) }));
};
exports.ModalViewContent = ModalViewContent;
function ModalWrapper(props) {
    const routeParams = props.route?.params || {};
    const combinedProps = { ...props, ...routeParams };
    const isModalOpen = combinedProps.open ?? true;
    const shouldUseNativeModal = combinedProps.useNativeModal || !combinedProps.navigation;
    if (!isModalOpen)
        return null;
    if (shouldUseNativeModal) {
        return ((0, jsx_runtime_1.jsx)(react_native_1.Modal, { visible: isModalOpen, transparent: true, animationType: "fade", onRequestClose: combinedProps.onClose, statusBarTranslucent: true, onShow: combinedProps.onShow, children: (0, jsx_runtime_1.jsx)(exports.ModalViewContent, { ...combinedProps, children: combinedProps.children }) }));
    }
    return ((0, jsx_runtime_1.jsx)(exports.ModalViewContent, { ...combinedProps, children: combinedProps.children }));
}
ModalWrapper.Heading = function ModalHeading({ children, style, ...rest }) {
    return (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.headingText, style], ...rest, children: children });
};
ModalWrapper.Description = function ModalDescription({ children, style, ...rest }) {
    return (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.descriptionText, style], ...rest, children: children });
};
const styles = react_native_1.StyleSheet.create({
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 100000,
        elevation: 100000,
    },
    headingText: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 4,
    },
    descriptionText: {
        fontSize: 13,
        opacity: 0.7,
    },
    snackbarContent: {
        flexDirection: 'row',
        alignItems: 'center',
        alignContent: 'center',
        flex: 1,
        display: 'flex',
    },
});
